import { cn, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@newfront-insurance/core-ui/v2';
import { ExternalLink, X } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { BrowserLiveView } from './browser-live-view-lazy';

// Kept intentionally lightweight vs the artifact preview panel — a browser
// session isn't a downloadable file. This module only needs: (1) open with a
// live-view URL and title, (2) render the DCV live stream (or a screenshot
// fallback) pinned to the right side of the chat container, (3) close on X or
// Esc.

export interface BrowserPreviewPayload {
  /** Signed live-view URL — AgentCore returns a SigV4-presigned DCV endpoint. */
  liveViewUrl?: string;
  /** How to render `liveViewUrl`. `'dcv'` decodes via the DCV Web Client SDK. */
  viewerType?: 'dcv';
  /** Screenshot to fall back to when no live view (e.g. session already closed). */
  screenshotUrl?: string;
  /** Header title. Usually the browserUse task or the current step summary. */
  title: string;
  /** Optional short subtitle (current URL, action summary). */
  subtitle?: string;
}

interface BrowserPreviewContextValue {
  open: (payload: BrowserPreviewPayload) => void;
  close: () => void;
  isOpen: boolean;
}

const BrowserPreviewContext = createContext<BrowserPreviewContextValue | null>(null);

/** Optional accessor — returns null when the provider isn't mounted (e.g. in message renderers used outside the chat shell). */
export function useBrowserPreviewSafe(): BrowserPreviewContextValue | null {
  return useContext(BrowserPreviewContext);
}

const PANEL_WIDTH = 640;

export function BrowserPreviewProvider({ children }: { children: ReactNode }) {
  const [payload, setPayload] = useState<BrowserPreviewPayload | null>(null);

  const open = useCallback((next: BrowserPreviewPayload) => {
    setPayload(next);
  }, []);

  const close = useCallback(() => {
    setPayload(null);
  }, []);

  useEffect(() => {
    if (!payload) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      close();
    };
    document.addEventListener('keydown', onKey, { capture: true });
    return () => document.removeEventListener('keydown', onKey, { capture: true });
  }, [payload, close]);

  const value = useMemo<BrowserPreviewContextValue>(
    () => ({ open, close, isOpen: payload !== null }),
    [open, close, payload],
  );

  return (
    <BrowserPreviewContext.Provider value={value}>
      <div className="relative flex min-h-0 min-w-0 flex-1 overflow-hidden">
        <div className="flex min-w-0 flex-1 flex-col">{children}</div>
        {payload ? (
          <TooltipProvider delayDuration={0}>
            <aside
              aria-label="Browser session preview"
              className={cn(
                'border-steel-75 relative flex h-full min-w-0 shrink-0 flex-col overflow-hidden border-l bg-white',
              )}
              style={{ width: PANEL_WIDTH }}
            >
              <div className="border-steel-75 flex h-14 shrink-0 items-center justify-between gap-2 border-b bg-white px-4">
                <div className="flex min-w-0 flex-col">
                  <div className="text-steel-500 truncate text-xs font-semibold">{payload.title}</div>
                  {payload.subtitle ? (
                    <div className="text-steel-400 truncate text-[11px]">{payload.subtitle}</div>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {payload.liveViewUrl && payload.viewerType !== 'dcv' ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <a
                          href={payload.liveViewUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Open live view in a new tab"
                          className="text-steel-400 hover:bg-steel-100 hover:text-steel-500 flex size-6 items-center justify-center rounded-sm transition-colors"
                        >
                          <ExternalLink className="size-4" />
                        </a>
                      </TooltipTrigger>
                      <TooltipContent>Open in new tab</TooltipContent>
                    </Tooltip>
                  ) : null}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        aria-label="Close browser preview"
                        className="text-steel-400 hover:bg-steel-100 hover:text-steel-500 flex size-6 items-center justify-center rounded-sm transition-colors"
                        onClick={close}
                      >
                        <X className="size-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>Close</TooltipContent>
                  </Tooltip>
                </div>
              </div>
              <div className="bg-steel-50 min-h-0 flex-1 p-3">
                {/* eslint-disable-next-line no-nested-ternary */}
                {payload.liveViewUrl && payload.viewerType === 'dcv' ? (
                  <div className="border-steel-75 h-full w-full overflow-hidden rounded border bg-white">
                    <BrowserLiveView signedUrl={payload.liveViewUrl} />
                  </div>
                ) : payload.screenshotUrl ? (
                  // eslint-disable-next-line jsx-a11y/img-redundant-alt
                  <img
                    src={payload.screenshotUrl}
                    alt={payload.title}
                    className="border-steel-75 h-full w-full rounded border bg-white object-contain"
                  />
                ) : (
                  <div className="text-steel-400 flex h-full items-center justify-center text-xs">
                    Waiting for browser preview…
                  </div>
                )}
              </div>
            </aside>
          </TooltipProvider>
        ) : null}
      </div>
    </BrowserPreviewContext.Provider>
  );
}
